import type { MouseEvent } from "react";
import type { WebsiteDefinition } from "@baze/schema";

/**
 * Link clicks inside a draft preview, as a visitor would get them but without leaving the app:
 * - `#` (the one-page home link) scrolls back to the top of the site;
 * - `#anchor` scrolls to that section (invitation openings are left to the invitation script; lesson
 *   topics change the URL hash so their CSS :target detail opens);
 * - `/page` and `/page#anchor` switch the previewed page through `go`, then scroll to the anchor;
 * - http(s) links (WhatsApp included) open in a new tab; tel: and mailto: keep their default.
 */
export function handlePreviewLink(e: MouseEvent<HTMLElement>, def: WebsiteDefinition | undefined, go: (slug: string) => void) {
  const a = (e.target as HTMLElement).closest("a");
  if (!a) return;
  const href = a.getAttribute("href") ?? "";
  if (/^(tel|mailto):/i.test(href)) return;
  if (href.startsWith("#")) {
    if (a.closest("[data-baze-open]") && document.querySelector("[data-baze-gate]")) return;
    e.preventDefault();
    // Lesson topics open with CSS :target, which only a real URL hash change updates.
    if (a.closest(".baze-lessons")) {
      window.location.hash = href;
      return;
    }
    if (href === "#") scrollToTop(a);
    else scrollToAnchor(href.slice(1));
    return;
  }
  e.preventDefault();
  if (href.startsWith("/") && !href.startsWith("//")) {
    const [path, hash] = href.split("#");
    const target = path || "/";
    if (!def?.pages.some((p) => p.slug === target)) return;
    // Starts from the top, as a page load would (also when `target` is the page already shown).
    if (!hash) scrollToTop(a);
    go(target);
    if (hash) requestAnimationFrame(() => scrollToAnchor(hash));
    return;
  }
  if (/^https?:/i.test(href)) window.open(href, "_blank", "noopener,noreferrer");
}

const behavior = (): ScrollBehavior => (matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth");

/** The nearest scrolling ancestor of the site (device frame, editor canvas) or the window goes back to 0. */
function scrollToTop(from: Element) {
  from.closest("details[open]")?.removeAttribute("open");
  let el = from.closest(".baze-site")?.parentElement ?? null;
  for (; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) break;
  }
  (el ?? window).scrollTo({ top: 0, behavior: behavior() });
}

function scrollToAnchor(id: string) {
  const el = id ? document.getElementById(decodeURIComponent(id)) : null;
  el?.scrollIntoView({ behavior: behavior() });
}
